import React from 'react';
import { SparklesIcon, HeartIcon, MoonIcon, SunIcon, StarIcon, ShieldCheckIcon, ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { FONTS, WA_NUMBER, HeroCarousel, BenefitsCTA, ActivitiesCTA, PhotoGallery, ReadyToStart } from './ServicePageTemplate';
import { Link } from 'react-router-dom';

// ─── IMPORT SAUNA PHOTOS (using your sauna pics) ─────────────────────────────
import saunaPic from '../../assets/images/sauna pic.png';
import saunaPic2 from '../../assets/images/sauna pic 2.png';
import saunaPic3 from '../../assets/images/sauna pic 3.png';

// ─── PHOTOS ARRAY FOR HERO SLIDER (using sauna pics) ─────────────────────────
const HERO_PHOTOS = [saunaPic, saunaPic2, saunaPic3];

// ─── PHOTOS FOR GALLERY - Using only sauna pics ──────────────────────────────
const GALLERY_PHOTOS = [saunaPic, saunaPic2, saunaPic3, saunaPic, saunaPic2, saunaPic3];

const DATA = {
  tagline:   'HEAT. HEAL. RESTORE.',
  heroText: 'Step into the heat. Sweat, recover and reset in our authentic Finnish dry sauna.',
  waMessage: encodeURIComponent("Hi PowerCore! 🔥 I'm interested in the Sauna facility. Could you tell me more about access, pricing and session times?"),

  features: [
    { icon: <SparklesIcon className="h-6 w-6" />, title: 'Finnish Dry Sauna',       desc: 'Authentic kiln-dried Finnish sauna reaching 80–100°C. Traditional löyly steam can be added by pouring water over heated rocks for an intense, purifying, deeply therapeutic experience.' },
    { icon: <HeartIcon className="h-6 w-6" />,    title: 'Cardiovascular Benefits', desc: 'Regular sauna use improves circulation, lowers blood pressure and mimics the cardiovascular effects of moderate exercise — making it a powerful addition to any wellness routine.' },
    { icon: <MoonIcon className="h-6 w-6" />,     title: 'Deep Muscle Recovery',   desc: 'Heat penetrates deep into muscle tissue, accelerating recovery from intense training, reducing DOMS and flushing out lactic acid for dramatically faster turnaround between sessions.' },
    { icon: <SunIcon className="h-6 w-6" />,      title: 'Skin & Detox',           desc: 'Profuse sweating opens pores, removes impurities and promotes cell renewal. Regular sauna sessions leave skin visibly glowing, cleaner and more elastic within weeks.' },
    { icon: <ShieldCheckIcon className="h-6 w-6" />, title: 'Immune Boost',        desc: 'The heat triggers a mild fever-like response, stimulating white blood cell production and progressively strengthening your immune system with consistent use.' },
    { icon: <StarIcon className="h-6 w-6" />,     title: 'Mental Wellness',        desc: 'The quiet, warm environment releases endorphins and growth hormone, reduces cortisol levels and promotes deep relaxation — leaving you calm, clear-headed and mentally recharged.' },
  ],

  benefits: [
    'Flush toxins through deep, cathartic sweating',
    'Accelerate post-workout muscle recovery dramatically',
    'Improve cardiovascular health naturally',
    'Promote deep, restful and restorative sleep',
    'Boost metabolism and passive calorie burn',
    'Radiant, cleaner skin through deep pore cleansing',
    'Reduce stress hormones and anxiety levels',
    'Strengthen immune system over time with regular use',
  ],
};

// Custom Features Grid with Sauna Background
const FeaturesGridWithBackground = ({ features, sectionTitle, sectionLabel }) => (
  <section className="py-16 relative overflow-hidden">
    {/* Background Image with saunaPic */}
    <div 
      className="absolute inset-0 bg-cover bg-center"
      style={{ 
        backgroundImage: `url(${saunaPic})`,
      }}
    />
    
    <div className="container-custom relative z-10">
      <div className="text-center mb-12">
        <span className="text-xs font-bold tracking-[0.2em] uppercase mb-2 inline-block text-amber-300">
          {sectionLabel}
        </span>
        <h2 className="font-black text-white"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(1.8rem,3.5vw,2.8rem)' }}>
          {sectionTitle}
        </h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {features.map((f, i) => (
          <div key={i}
            className="rounded-3xl p-7 transition-all duration-300 group"
            style={{ 
              background: 'rgba(0,0,0,0.75)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4 transition-all group-hover:scale-110"
              style={{ background: 'rgba(245,158,11,0.2)', color: '#fbbf24' }}>
              {f.icon}
            </div>
            <h3 className="font-black text-white mb-2"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.2rem' }}>
              {f.title.toUpperCase()}
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const SaunaService = () => (
  <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
    <style>{FONTS}</style>
    
    {/* 1. Hero Slider - Using sauna pics */}
    <HeroCarousel photos={HERO_PHOTOS} {...DATA} />
    
    {/* 2. Photo Gallery - EXPLORE SAUNA (second section after hero) */}
    <PhotoGallery photos={GALLERY_PHOTOS} title="EXPLORE SAUNA" />
    
    {/* 3. Features Grid with Sauna Background */}
    <FeaturesGridWithBackground 
      features={DATA.features} 
      sectionLabel="What We Offer" 
      sectionTitle="THE SCIENCE OF HEAT THERAPY" 
    />
    
    {/* 4. Benefits CTA */}
    <BenefitsCTA benefits={DATA.benefits} benefitsTitle="YOUR BODY WILL THANK YOU."
      ctaTitle="BOOK YOUR SESSION" ctaText="Message us on WhatsApp to book a sauna session, ask about pricing or find out the best times to visit the facility."
      waMessage={DATA.waMessage} />
    
    {/* 5. Activities CTA */}
    <ActivitiesCTA />
    
    {/* 6. Sauna Benefits, Hours & Pricing Section */}
    <div className="py-16 bg-gradient-to-br from-amber-900 to-orange-800">
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-amber-300 font-semibold tracking-wider uppercase text-sm">Wellness Investment</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 mt-2">
              READY TO EXPERIENCE <span className="text-amber-300">THE HEAT?</span>
            </h2>
            <p className="text-amber-100 mb-6 leading-relaxed">
              Whether you're looking to recover after an intense workout, detoxify your body, 
              or simply unwind and de-stress, our authentic Finnish sauna is the perfect addition 
              to your wellness routine.
            </p>
            
            {/* Sauna Hours */}
            <div className="mb-6 p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
              <div className="flex items-center gap-2 mb-3">
                <ClockIcon className="h-5 w-5 text-amber-300" />
                <h3 className="text-white font-bold text-lg">Sauna Operating Hours</h3>
              </div>
              <div className="space-y-2 text-amber-100">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Monday - Saturday:</span>
                  <span>5:00 AM - 8:00 AM &amp; 5:00 PM - 8:00 PM</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Sunday:</span>
                  <span>8:00 AM - 8:00 PM</span>
                </div>
              </div>
              <p className="text-amber-300/70 text-xs mt-3 italic">*Last session 30 minutes before closing</p>
            </div>
            
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3 text-white">
                <span className="text-amber-300 text-xl">✓</span>
                <span>Included with all premium memberships</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <span className="text-amber-300 text-xl">✓</span>
                <span>Day passes available for non-members</span>
              </li>
              <li className="flex items-center gap-3 text-white">
                <span className="text-amber-300 text-xl">✓</span>
                <span>Towels and refreshments available</span>
              </li>
            </ul>
            <Link 
              to="/pricing" 
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 px-8 rounded-full transition-all duration-300 hover:scale-105"
            >
              View Pricing <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img 
              src={saunaPic2} 
              alt="Sauna interior at PowerCore Fitness" 
              className="rounded-2xl shadow-xl h-48 w-full object-cover"
            />
            <img 
              src={saunaPic3} 
              alt="Sauna relaxation area" 
              className="rounded-2xl shadow-xl h-48 w-full object-cover mt-8"
            />
          </div>
        </div>
      </div>
    </div>
    
    {/* 7. Ready to Start CTA */}
    <ReadyToStart />
  </div>
);

export default SaunaService;